import { useEffect, useRef } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { useInView } from "react-intersection-observer";

function renderFaqAnswer(text: string): JSX.Element {
  // Minimal markdown link support: [Label](https://example.com)
  const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const parts: Array<string | JSX.Element> = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const label = match[1];
    const url = match[2];

    parts.push(
      <a key={`${match.index}-${url}`} href={url} target="_blank" rel="noopener noreferrer">
        {label}
      </a>,
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // Preserve manual line breaks from markdown imports.
  return <span className="whitespace-pre-line">{parts}</span>;
}

interface FaqItem {
  question: string;
  answer: string;
}

function FaqBlockNodeView({ node }: NodeViewProps) {
  const faqs: FaqItem[] = node.attrs.faqs || [];
  
  // Filter out empty FAQs
  const validFaqs = faqs.filter(faq => faq.question.trim() && faq.answer.trim());

  // ⚠️ ANALYTICS: Inline section visibility tracking
  const hasTrackedView = useRef(false);
  const { ref: visibilityRef, inView } = useInView({ threshold: 0.5, triggerOnce: true });

  useEffect(() => {
    if (inView && !hasTrackedView.current && validFaqs.length > 0) {
      hasTrackedView.current = true;
      trackEvent(ANALYTICS_EVENTS.SECTION_VIEWED, {
        section_id: 'article-faq',
        section_name: 'Article FAQ',
        section_page: 'Article',
        section_position: 90,
        faq_count: validFaqs.length,
      });
    }
  }, [inView]);

  if (validFaqs.length === 0) {
    return null;
  }

  return (
    <NodeViewWrapper className="faq-block-wrapper" data-type="faq-block">
      <div ref={visibilityRef} className="faq-block">
        <div className="faq-block-title">Frequently Asked Questions</div>
        <Accordion type="single" collapsible className="w-full">
          {validFaqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`faq-${index}`} 
              className="faq-accordion-item border-b"
            >
              <AccordionTrigger
                className="faq-accordion-trigger text-left"
                onClick={() => trackEvent(ANALYTICS_EVENTS.FAQ_OPENED, {
                  faq_question: faq.question,
                  faq_index: index,
                  article_url: typeof window !== 'undefined' ? window.location.pathname : '',
                })}
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="faq-accordion-content">
                {renderFaqAnswer(faq.answer)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </NodeViewWrapper>
  );
}

export interface FaqBlockNodeOptions {
  HTMLAttributes: Record<string, any>;
}

export const FaqBlockNode = Node.create<FaqBlockNodeOptions>({
  name: "faqBlock",

  group: "block",

  atom: true,

  selectable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      faqs: {
        default: [],
        parseHTML: (element) => {
          const data = element.getAttribute("data-faqs");
          if (data) {
            try {
              return JSON.parse(data);
            } catch {
              return [];
            }
          }
          return [];
        },
        renderHTML: (attributes) => {
          return { "data-faqs": JSON.stringify(attributes.faqs) };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-type='faq-block']",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, {
        "data-type": "faq-block",
        "data-faqs": JSON.stringify(HTMLAttributes.faqs),
        class: "faq-block",
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FaqBlockNodeView);
  },
});

export default FaqBlockNode;
